import React, {
	Dispatch,
	SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useRef,
} from 'react';
import { Provider } from 'react-redux';
import * as ReactDOM from 'react-dom';
import { GoogleMap, Polyline, useLoadScript } from '@react-google-maps/api';
import { ApolloProvider } from '@apollo/client';

import { store } from 'store';

import { ALL_LAYERS } from 'modules/AwdMapView/constants';
import {
	ALERT_ICONS,
	DRY_PATH_COLOR,
	LIVE_MACHINES_REFRESH_TIME,
	selectedMapItemColor,
} from 'modules/Telematics/constants';

import {
	Layer,
	TileCoords,
	Library,
	ILiveMachineData,
	IMachineIcon,
	PointData,
} from 'interfaces/maps.interface';

import { GET_PLACES_MATRIX_BY_TILES } from 'GraphQL/queries/mapService';

import TileUnit from 'sharedMapComponents/TileUnit';

import {
	constants,
	libraries,
	mapContainerStyle,
	MapTypesID,
} from 'utils/constants';
import debounce from 'utils/debounce';
// import { closeCurrentBrowserTab } from 'utils/helpers';

import { useSearchParams } from 'customHooks/useSearchParams';
import useApolloClientAndMsalAuth from 'customHooks/useApolloClientAndMsalAuth';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import Loader from 'sharedComponents/Loader';
// import AlertModal from 'widgets/AlertModal';

// import {
// 	setSnackbarMessage,
// 	setSnackbarSeverity,
// } from 'reducers/global.reducer';
import { redirect } from 'actions/navigation';

import { getUIClient, latLngConversion } from 'utils/helpers';
import extractValuesForPlaceTag from 'utils/extractValuesForPlaceTag';
// import deriveSelectedLayerGeometryType from 'utils/deriveSelectedLayerGeometryType';
import { smoothlyPanMapsTo } from 'utils/customPanTo';

import TelematicsPolygons from 'modules/Telematics/components/TelematicsPolygons';
import Search from 'sharedMapComponents/Search';
import {
	PlaceTag,
	PointGeometry,
	// PolygonGeometry,
} from 'interfaces/vector.interface';
import {
	resetLiveMachinesData,
	// removeLiveMachineData,
	setLiveMachinesData,
	setSelectedLiveMachine,
} from 'reducers/telematics.reducer';
import { createTagsFromArrayData } from 'modules/Telematics/components/GoogleMapView/utils';
import GenericMarker from 'sharedMapComponents/GenericMarker';
import { getIconSizes } from 'sharedMapComponents/GenericMarker/utils';
import MachineDataInfoWindow from 'modules/Telematics/components/MachineDataInfoWindow';
import PrunedPointInfoWindow from 'modules/Telematics/components/PrunedPointInfoWindow';
import { PointTagDataInterface } from 'modules/Telematics/types';

export const mapOptions = {
	disableDefaultUI: false,
	draggable: true,
	zoomControl: true,
	rotateControl: true,
	streetViewControl: false,
	fullscreenControl: false,
	keyboardShortcuts: false,
	mapTypeControl: true,
	disableDoubleClickZoom: true,
	mapTypeId: 'hybrid',
	mapTypeIds: [...MapTypesID],
	maxZoom: 19,
	minZoom: 1,
};

interface GoogleMapViewProps {
	handleClick?: (e: google.maps.MapMouseEvent) => void;
	handleDblClick?: (e: google.maps.MapMouseEvent) => void;
	handleZoomChange: () => void;
	isUserOnTab: boolean | Dispatch<SetStateAction<boolean>>;
	mapsRef: any;
	machinePointsData: Record<string, PointData>;
	machinePolygonData: any;
	onMapLoadComplete?: Function;
	liveMachinesRef: any;
	selectedPolygon: any;
	tilesPlaceIdsRef: any;
	updateGoogleRef: (a: typeof google.maps) => void;
	zoom: number;
	dryRunData: Array<{
		placeId: string | number;
		geometry: { lat: number; lng: number }[];
	}>;
	polyPathData: any;
	selectedDryPath: undefined | string | number;
}

const center = { lat: 30.473887, lng: 74.4229733 };
const validZoomValues = [17, 18, 19];

export const checkIsMachineMoving = (tags: PlaceTag[]) => {
	let isMachineMovingFlag = false;
	for (let i = 0; i < tags.length; i++) {
		let { TagPropertyKey: tagKey, value: tagValue }: PlaceTag = tags[i];
		if (tagKey === 'SPEED') {
			let speedValues = extractValuesForPlaceTag(tagValue);
			isMachineMovingFlag = speedValues[0] > 0.1;
			break;
		}
	}
	return isMachineMovingFlag;
};

const GoogleMapView = ({
	handleClick,
	handleDblClick,
	handleZoomChange,
	isUserOnTab,
	mapsRef,
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	machinePointsData,
	machinePolygonData,
	onMapLoadComplete,
	liveMachinesRef,
	selectedPolygon,
	tilesPlaceIdsRef,
	updateGoogleRef,
	zoom,
	dryRunData,
	polyPathData,
	selectedDryPath,
}: GoogleMapViewProps): JSX.Element => {
	const searchParams = useSearchParams();
	const dispatch = useAppDispatch();
	const { client } = useApolloClientAndMsalAuth();
	const timerRef: any = useRef(null);

	// const centerRef = useRef({ ...center });

	const { isLoaded } = useLoadScript({
		googleMapsApiKey: `${constants('GOOGLE_MAPS_API_KEY')}`,
		libraries: libraries as Library[],
	});
	const activeTagFilters = useAppSelector(
		(s) => s.telematics.activeTagFilters,
	);
	let lat = useAppSelector((s) => s.router.location.query.lat);
	let lng = useAppSelector((s) => s.router.location.query.lng);
	lat = ['', null, undefined].includes(lat) ? center.lat : Number(lat);
	lng = ['', null, undefined].includes(lng) ? center.lng : Number(lng);
	const machineId: string = useAppSelector(
		(s) => s.router.location.query.machineId,
	);
	const startDate = useAppSelector((s) => s.router.location.query.startDate);
	const endDate = useAppSelector((s) => s.router.location.query.endDate);
	const rawData = useAppSelector((s) => s.router.location.query.rawData);
	const liveMachinesData = useAppSelector(
		(s) => s.telematics.liveMachinesData,
	);
	const activeAlertFilters = useAppSelector(
		(s) => s.telematics.activeAlertFilters,
	);

	const liveMachinesDataList = useMemo(
		() =>
			Object.values(liveMachinesData).filter(
				(el) =>
					activeAlertFilters.size === 0 ||
					activeAlertFilters.has(el.machineIcon.iconType),
			),
		[liveMachinesData, activeAlertFilters],
	);

	const machinePointsDataValues = useMemo(
		() => Object.values(machinePointsData),
		[machinePointsData],
	);

	// const [isCursorOverPoint, setIsCursorOverPoint] = useState<boolean>(false);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const updateUrlParams = useCallback(
		(params: any) => {
			searchParams.set('lat', params.lat);
			searchParams.set('lng', params.lng);
			searchParams.set('startDate', startDate);
			searchParams.set('endDate', endDate);
			searchParams.set('machineId', machineId || '');
			searchParams.set('rawData', rawData || false);
			const newPath = `telematics?${searchParams.toString()}`;
			dispatch(redirect(newPath));
		},
		[dispatch, endDate, machineId, rawData, searchParams, startDate],
	);

	const updateCenterRef = useCallback(
		debounce(() => {
			if (mapsRef.current !== null) {
				const newCenter = mapsRef.current.getCenter();
				const newLat = newCenter.lat();
				const newLng = newCenter.lng();
				// centerRef.current.lat = newLat;
				// centerRef.current.lng = newLng;
				let newParams: any = { lat: newLat, lng: newLng };
				// if (!['', null].includes(farmId)) newParams.farmId = farmId;
				// if (!['', null].includes(eventId)) newParams.eventId = eventId;
				// if (!['', null].includes(contentId))
				// 	newParams.contentId = contentId;
				// if (!['', null].includes(seasonId))
				// 	newParams.seasonId = seasonId;
				updateUrlParams(newParams);
			}
		}, 1000),
		[endDate, machineId, rawData, searchParams, startDate],
	);

	// const getPlaceTagFilters = useCallback(
	// 	(selectedLayer: Layer) => {
	// 		const layerKey = `${selectedLayer.PlaceNamespace}-${selectedLayer.PlaceSourceType}-${selectedLayer.PlaceVisibleType}`;
	// 		return filters[layerKey];
	// 	},
	// 	[filters],
	// );

	// const shouldApplyTagFilters = useCallback(
	// 	(selectedLayer: Layer): boolean => {
	// 		const layerKey = `${selectedLayer.PlaceNamespace}-${selectedLayer.PlaceSourceType}-${selectedLayer.PlaceVisibleType}`;
	// 		if (suggestionLayers.includes(layerKey)) return false;
	// 		return true;
	// 	},
	// 	[suggestionLayers],
	// );

	const getVariables = useCallback(
		(coord: TileCoords, selectedLayer: Layer, zoom = 19) => {
			return {
				input: {
					tileRequests: {
						env: {
							sourceType: selectedLayer?.PlaceSourceType,
							namespace: selectedLayer?.PlaceNamespace,
							visibleType: selectedLayer?.PlaceVisibleType,
							client: getUIClient(),
						},
						mapTiles: [
							{
								zoom: zoom,
								tileX: coord.x,
								tileY: coord.y,
							},
						],
						tagFilterV3: activeTagFilters,
						// tagFilterV2: shouldApplyTagFilters(selectedLayer)
						// 	? getPlaceTagFilters(selectedLayer)
						// 	: {},
					},
				},
			};
		},
		[activeTagFilters],
	);

	const getLiveMachineIcon = useCallback((tags: PlaceTag[]): IMachineIcon => {
		let highestRankFound = 100;
		let currentRank = 0;
		let icon: IMachineIcon = { url: '', iconType: 'DEFAULT', desc: '' };
		for (let i = 0; i < tags.length; i++) {
			let { TagPropertyKey: tagKey, value: tagValue }: PlaceTag = tags[i];
			let tempTagValues = extractValuesForPlaceTag(tagValue);
			// *NOTE*: do not change this order
			if (
				tagKey === 'ACCIDENT_STATUS' &&
				tempTagValues.length > 0 &&
				(tempTagValues.includes('HIGH_G_FORCE') ||
					tempTagValues.includes('ROLLOVER'))
			) {
				currentRank = 1;
				if (currentRank <= highestRankFound) {
					highestRankFound = currentRank;
					icon = { ...ALERT_ICONS['TRIANGLE-RED'] };
				}
			} else if (
				tagKey === 'EXTERNAL_POWER_STATUS' &&
				tempTagValues.length > 0
			) {
				if (tempTagValues.includes('EXTERNAL_POWER_REMOVED')) {
					currentRank = 2;
					if (currentRank <= highestRankFound) {
						highestRankFound = currentRank;
						icon = { ...ALERT_ICONS['SQUARE-RED'] };
					}
				}
			} else if (
				tagKey === 'EXTERNAL_POWER_CONNECTED' &&
				tempTagValues.length > 0
			) {
				if (tempTagValues.includes(false)) {
					currentRank = 3;
					if (currentRank <= highestRankFound) {
						highestRankFound = currentRank;
						const isMachineMovingFlag = checkIsMachineMoving(tags);
						if (isMachineMovingFlag) {
							icon = { ...ALERT_ICONS['ARROW-RED'] };
						} else {
							icon = { ...ALERT_ICONS['CIRCLE-RED'] };
						}
					}
				}
			} else if (
				tagKey === 'INTERNAL_BATTERY_STATUS' &&
				tempTagValues.length > 0
			) {
				if (
					tempTagValues.includes('LOW_BATTERY') ||
					tempTagValues.includes('INTERNAL_BATTERY_TAMPERED')
				) {
					currentRank = 4;
					if (currentRank <= highestRankFound) {
						highestRankFound = currentRank;
						const isMachineMovingFlag = checkIsMachineMoving(tags);
						if (isMachineMovingFlag) {
							icon = { ...ALERT_ICONS['ARROW-YELLOW'] };
						} else {
							icon = { ...ALERT_ICONS['CIRCLE-YELLOW'] };
						}
					}
				}
			} else if (
				tagKey === 'IGNITION_ON' &&
				tempTagValues.length > 0 &&
				tempTagValues.includes(false)
			) {
				currentRank = 5;
				if (currentRank <= highestRankFound) {
					highestRankFound = currentRank;
					icon = { ...ALERT_ICONS['CIRCLE-PINK'] };
				}
			} else if (
				tagKey === 'SPRAYER_ON' &&
				tempTagValues.length > 0 &&
				tempTagValues.includes(true)
			) {
				currentRank = 6;
				if (currentRank <= highestRankFound) {
					highestRankFound = currentRank;
					const isMachineMovingFlag = checkIsMachineMoving(tags);
					if (isMachineMovingFlag) {
						icon = { ...ALERT_ICONS['ARROW-BLUE'] };
					} else {
						icon = { ...ALERT_ICONS['SQUARE-BLUE'] };
					}
				}
			} else if (
				tagKey === 'SPEED' &&
				tempTagValues.length > 0 &&
				tempTagValues[0] > 0.1
			) {
				currentRank = 7;
				if (currentRank <= highestRankFound) {
					highestRankFound = currentRank;
					icon = { ...ALERT_ICONS['ARROW-GREEN'] };
				}
			} else if (
				tagKey === 'IGNITION_ON' &&
				tempTagValues.length > 0 &&
				tempTagValues.includes(true)
			) {
				currentRank = 8;
				if (currentRank <= highestRankFound) {
					highestRankFound = currentRank;
					icon = { ...ALERT_ICONS['SQUARE-GREEN'] };
				}
			}
		}
		return icon;
	}, []);

	const updateSelectedMachineValue = useCallback(
		(deviceMachineId: string, key: string) => {
			if (deviceMachineId === machineId) {
				dispatch(setSelectedLiveMachine(key));
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[machineId],
	);

	const checkForDuplicateKeysAndUpdatePayload = useCallback(
		(
			payload: Record<string, ILiveMachineData>,
			placeData: any,
			coord: TileCoords,
			selectedLayer: Layer,
			tagJson: any,
		) => {
			const { type, geometry } = latLngConversion(placeData.geometry);
			const key = `${placeData.placeId}-${selectedLayer.PlaceNamespace}-${selectedLayer.PlaceSourceType}-${selectedLayer.PlaceVisibleType}`;
			let temp: ILiveMachineData;
			if (payload[key]) {
				temp = {
					...payload[key],
					machineIcon: getLiveMachineIcon(placeData.tags),
					placeTags: placeData.tags as PlaceTag[],
					placeGeometryResult: {
						geometryType: type as string,
						geometry: geometry as PointGeometry,
					},
					parentTiles: new Set(payload[key].parentTiles),
					timeRange: placeData.timeRange,
					tagJson,
				};
			} else {
				temp = {
					machineId: placeData.machineNumber,
					machineIcon: getLiveMachineIcon(placeData.tags),
					placeId: placeData.placeId as string,
					placeGeometryResult: {
						geometryType: type as string,
						geometry: geometry as PointGeometry,
					},
					placeTags: placeData.tags as PlaceTag[],
					parentTiles: new Set(),
					referenceAttributes: [],
					// 	placeData.referenceAttributes as ReferenceAttribute[],
					selectedLayer,
					timeRange: placeData.timeRange,
					tagJson,
				};
				updateSelectedMachineValue(placeData.machineNumber, key);
			}
			temp.parentTiles?.add(`${coord.x}-${coord.y}`);
			payload[key] = temp;
		},
		[getLiveMachineIcon, updateSelectedMachineValue],
	);

	const updateMapTilesData = useCallback(
		(mapResults: any[], selectedLayer: Layer, coord: TileCoords) => {
			let placesResult = mapResults[0]?.placesMatrix;
			if (placesResult.placeId.length < 1) return;
			// if (placesResult === undefined || placesResult?.length < 1) return;

			let payload: Record<string, ILiveMachineData> = {
				...liveMachinesRef.current,
			};

			if (
				tilesPlaceIdsRef.current[`${coord.x}-${coord.y}`] === undefined
			) {
				tilesPlaceIdsRef.current[`${coord.x}-${coord.y}`] = new Set();
			}
			const refJson: any = JSON.parse(placesResult.refJson);
			const tagJson: any = JSON.parse(placesResult.tagJson);
			const allTags = createTagsFromArrayData(tagJson);
			const machineNumberArray: any =
				refJson['MACHINE_REF_TYPE::MACHINE_NUMBER::VALUE_TYPE_STRING'];
			placesResult.placeId.forEach((_: any, index: any) => {
				const key = `${placesResult.placeId[index]}-${selectedLayer.PlaceNamespace}-${selectedLayer.PlaceSourceType}-${selectedLayer.PlaceVisibleType}`;
				tilesPlaceIdsRef.current[`${coord.x}-${coord.y}`].add(key);
				const placeData = {
					geometry: placesResult.geometry[index],
					placeId: placesResult.placeId[index],
					timeRange: {
						startTime: {
							seconds: placesResult.startTime[index],
						},
						endTime: {
							seconds: placesResult.endTime[index],
						},
					},
					tags: allTags[index],
					machineNumber: machineNumberArray[index],
				};

				checkForDuplicateKeysAndUpdatePayload(
					payload,
					placeData,
					coord,
					selectedLayer,
					tagJson,
				);
			});
			dispatch(
				setLiveMachinesData(
					payload as Record<string, ILiveMachineData>,
				),
			);
		},
		[
			liveMachinesRef,
			tilesPlaceIdsRef,
			dispatch,
			checkForDuplicateKeysAndUpdatePayload,
		],
	);

	// const updateIsCursorOverPoint = useCallback(
	// 	(val: boolean): void => {
	// 		setIsCursorOverPoint(val);
	// 	},
	// 	[setIsCursorOverPoint],
	// );

	const pruneTilesData = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		(tile: string, selectedLayer: Layer) => {
			// let tilesWithData: string[] = Array.from(
			// 	tilesPlaceIdsRef.current[tile] ?? [],
			// );
			// tilesWithData.forEach((el: string) => {
			// 	tilesPlaceIdsRef.current[tile].delete(el);
			// 	dispatch(removeLiveMachineData(el));
			// });
			// let tempLiveMachinesData = { ...liveMachinesRef.current };
			// let tilesWithData: string[] = Array.from(
			// 	tilesPlaceIdsRef.current[tile] ?? [],
			// );
			// tilesWithData.forEach((el: string) => {
			// 	console.log(
			// 		'deleting parent tile',
			// 		liveMachinesRef.current,
			// 		el,
			// 		tempLiveMachinesData?.parentTiles,
			// 	);
			// 	let currentMachineParentTiles = new Set(
			// 		tempLiveMachinesData?.parentTiles,
			// 	);
			// 	currentMachineParentTiles.delete(el);
			// 	console.log('after', currentMachineParentTiles);
			// 	let currentMachine = JSON.parse(
			// 		JSON.stringify(tempLiveMachinesData[el]),
			// 	);
			// 	currentMachine.parentTiles = currentMachineParentTiles;
			// 	tempLiveMachinesData[el] = currentMachine;
			// 	liveMachinesRef.current = tempLiveMachinesData;
			// 	tilesPlaceIdsRef.current[tile].delete(el);
			// 	// TODO: only delete if parent-tiles size is 0
			// 	if (liveMachinesRef.current[el]?.parentTiles.size === 0) {
			// 		dispatch(removeLiveMachineData(el));
			// 	}
			// });
		},
		[dispatch, tilesPlaceIdsRef],
	);

	const onTileLoadCallCompleted = useCallback(
		(data: any, selectedLayer: Layer, coord: TileCoords): void => {
			const mapResults: any[] =
				data?.getPlacesMatrixByTiles?.tileResponses;
			if (mapResults?.length) {
				updateMapTilesData(mapResults, selectedLayer, coord);
			}
		},
		[updateMapTilesData],
	);

	const addLayersToMap = useCallback(
		(layers: string[]) => {
			layers.forEach((layer: string) => {
				mapsRef.current?.overlayMapTypes?.push(
					new CoordMapType(
						client,
						getVariables,
						onTileLoadCallCompleted,
						pruneTilesData,
						ALL_LAYERS[layer],
						new google.maps.Size(256, 256),
					),
				);
			});
		},
		[
			client,
			getVariables,
			mapsRef,
			onTileLoadCallCompleted,
			pruneTilesData,
		],
	);

	const onSearch = ({
		lat,
		lng,
	}: {
		lat: number | string;
		lng: number | string;
	}) => {
		smoothlyPanMapsTo({
			mapInstance: mapsRef.current,
			mapOptions,
			lat,
			lng,
			zoom,
		});
	};

	const onLoad = useCallback(
		(mapInstance: google.maps.Map) => {
			mapsRef.current = mapInstance;
			mapsRef.current.setCenter({ lat, lng });
			onMapLoadComplete && onMapLoadComplete();
			addLayersToMap(['IOT_PINGS-IOT_GATEWAY_TYPE-LIVE_DEVICE_TRACES']);
		},
		[mapsRef, lat, lng, onMapLoadComplete, addLayersToMap],
	);

	const resetAndReinitialize = () => {
		if (
			mapsRef.current !== null &&
			mapsRef.current.overlayMapTypes?.length > 0
		) {
			mapsRef.current.overlayMapTypes.clear();
			addLayersToMap(['IOT_PINGS-IOT_GATEWAY_TYPE-LIVE_DEVICE_TRACES']);
			// mapsRef.current.overlayMapTypes.clear();
		}
	};

	useEffect(() => {
		if (isLoaded) {
			updateGoogleRef(google.maps);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoaded]);

	useEffect(() => {
		liveMachinesRef.current = liveMachinesData;
	}, [liveMachinesData, liveMachinesRef]);

	useEffect(() => {
		mapsRef.current !== null && mapsRef.current.setCenter({ lat, lng });
	}, [lat, lng, mapsRef]);

	useEffect(() => {
		if (!isUserOnTab) {
			clearInterval(timerRef.current);
		} else {
			clearInterval(timerRef.current);
			timerRef.current = setInterval(() => {
				resetAndReinitialize();
			}, LIVE_MACHINES_REFRESH_TIME);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isUserOnTab]);

	useEffect(() => {
		dispatch(resetLiveMachinesData());
		resetAndReinitialize();
		clearInterval(timerRef.current);
		timerRef.current = setInterval(() => {
			resetAndReinitialize();
		}, LIVE_MACHINES_REFRESH_TIME);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeTagFilters]);

	return (
		<div style={{ width: '100%', height: '100%' }}>
			{!isLoaded ? (
				<Loader loading />
			) : (
				<GoogleMap
					id="map"
					mapContainerStyle={mapContainerStyle}
					mapTypeId={google.maps.MapTypeId.HYBRID}
					options={mapOptions}
					onClick={handleClick}
					onDblClick={handleDblClick}
					onLoad={onLoad}
					onCenterChanged={updateCenterRef}
					onZoomChanged={handleZoomChange}
					zoom={zoom || 19}
				>
					{dryRunData.map((d, i: any) => {
						const isSelected = d.placeId === selectedDryPath;
						const SELECTED_CONFIG = {
							strokeColor: selectedMapItemColor,
							strokeOpacity: 2,
							strokeWeight: 4,
						};

						const DEFAULT_CONFIG = {
							strokeColor: DRY_PATH_COLOR,
							strokeOpacity: 1,
							strokeWeight: 2,
						};
						return (
							<Polyline
								key={i}
								path={d.geometry}
								options={
									isSelected
										? SELECTED_CONFIG
										: DEFAULT_CONFIG
								}
							/>
						);
					})}
					<TelematicsPolygons
						machinePolygonData={machinePolygonData}
						selectedPolygon={selectedPolygon}
					/>
					{polyPathData.map((d: any, i: any) => {
						return (
							<Polyline
								key={i}
								path={d}
								options={{
									strokeColor: 'blue',
									strokeOpacity: 1,
									strokeWeight: 2,
								}}
							/>
						);
					})}
					{validZoomValues.includes(zoom) &&
						machinePointsDataValues?.length > 0 &&
						machinePointsDataValues.map(
							(pointData: PointData, index: number) => {
								let headingAngle = 0;
								if (
									index <
									machinePointsDataValues.length - 1
								) {
									const lat1 =
										pointData.placeGeometryResult.geometry
											.lat;
									const lng1 =
										pointData.placeGeometryResult.geometry
											.lng;
									const lat2 =
										machinePointsDataValues[index + 1]
											.placeGeometryResult.geometry.lat;
									const lng2 =
										machinePointsDataValues[index + 1]
											.placeGeometryResult.geometry.lng;
									const point1 = new google.maps.LatLng(
										lat1,
										lng1,
									);
									const point2 = new google.maps.LatLng(
										lat2,
										lng2,
									);
									headingAngle =
										google.maps.geometry.spherical.computeHeading(
											point1,
											point2,
										);
								}
								const key = `${pointData.placeId}-${pointData.selectedLayer.PlaceNamespace}-${pointData.selectedLayer.PlaceSourceType}-${pointData.selectedLayer.PlaceVisibleType}`;
								const getHeadingAngleIcon =
									(): google.maps.Symbol => {
										const zoomToScale: {
											[x: number]: number;
										} = { 17: 1, 18: 2, 19: 2 };
										const scale = zoomToScale[zoom];
										return {
											path: google.maps.SymbolPath
												.FORWARD_CLOSED_ARROW,
											rotation: headingAngle,
											fillColor: 'red',
											fillOpacity: 1,
											scale,
										};
									};
								return (
									<GenericMarker
										key={key}
										handleClick={() => {}}
										icon={getHeadingAngleIcon()}
										position={
											pointData.placeGeometryResult
												.geometry
										}
									>
										<PrunedPointInfoWindow
											machineData={
												pointData.tagData as PointTagDataInterface
											}
										/>
									</GenericMarker>
								);
							},
						)}
					<Search onSearch={onSearch} />
					{liveMachinesDataList?.length > 0 &&
						liveMachinesDataList.map((liveMachineData) => {
							const key = `${liveMachineData.placeId}-${liveMachineData.selectedLayer?.PlaceNamespace}-${liveMachineData.selectedLayer?.PlaceSourceType}-${liveMachineData.selectedLayer?.PlaceVisibleType}`;
							const { lat, lng } =
								liveMachineData.placeGeometryResult.geometry;
							const iconSizes = getIconSizes(zoom);
							const icon = {
								...liveMachineData.machineIcon,
								...iconSizes.extraLarge,
							};

							const handleClick = () => {
								dispatch(setSelectedLiveMachine(key));
								searchParams.set(
									'machineId',
									liveMachineData.machineId,
								);
								searchParams.set('lat', `${lat}`);
								searchParams.set('lng', `${lng}`);
								const newPath = `telematics?${searchParams.toString()}`;
								dispatch(redirect(newPath));
							};
							return (
								<GenericMarker
									key={key}
									handleClick={handleClick}
									icon={icon}
									position={{ lat, lng }}
								>
									<MachineDataInfoWindow
										machineData={liveMachineData}
									/>
								</GenericMarker>
							);
						})}
				</GoogleMap>
			)}
		</div>
	);
};

export default React.memo(GoogleMapView);

class CoordMapType {
	/* eslint-disable */
	client: any;
	getVariables: (
		coord: TileCoords,
		selectedLayer: Layer,
		zoom: number,
	) => any;
	onTileLoadCallCompleted: (a: any, b: Layer, c: TileCoords) => void;
	pruneTilesData: (a: string, b: Layer) => void;
	selectedLayer: Layer | null;
	tileSize: google.maps.Size;
	/* eslint-enable */
	constructor(
		client: any,
		getVariables: (
			coord: TileCoords,
			selectedLayer: Layer,
			zoom: number,
		) => any,
		onTileLoadCallCompleted: (a: any, b: Layer, c: TileCoords) => void,
		pruneTilesData: (a: string, b: Layer) => void,
		selectedLayer: Layer | null,
		tileSize: google.maps.Size,
	) {
		this.tileSize = tileSize;
		this.getVariables = getVariables;
		this.selectedLayer = selectedLayer;
		this.onTileLoadCallCompleted = onTileLoadCallCompleted;
		this.pruneTilesData = pruneTilesData;
		this.client = client;
	}

	getTile(
		coord: google.maps.Point,
		zoom: number,
		ownerDocument: Document,
	): HTMLElement {
		const div = ownerDocument.createElement('div');
		ReactDOM.render(
			<ApolloProvider client={this.client}>
				<Provider store={store}>
					<TileUnit
						coord={coord}
						getVariables={this.getVariables}
						height={this.tileSize.height}
						onLoadCall={GET_PLACES_MATRIX_BY_TILES}
						onLoadCallCompleted={this.onTileLoadCallCompleted}
						pruneTilesData={this.pruneTilesData}
						selectedLayer={this.selectedLayer}
						width={this.tileSize.width}
						zoom={zoom}
					/>
				</Provider>
			</ApolloProvider>,
			div,
		);
		return div;
	}

	releaseTile(tile: Element): void {
		ReactDOM.unmountComponentAtNode(tile);
	}
}
